import React, { useMemo, useState } from "react";
import {
  useObligations,
  Obligation,
  Completion,
} from "../hooks/useObligations";
import {
  CalendarCheckIcon,
  CheckIcon,
  EditIcon,
  PlusIcon,
  SpinnerIcon,
  TrashIcon,
} from "@/components/icons";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { PanelList } from "@/components/ui/PanelList";
import { PanelListItem } from "@/components/ui/PanelListItem";
import { ObligationModal } from "./ObligationModal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";

const UNIT_LABELS: Record<string, string> = {
  WEEKS: "week",
  MONTHS: "month",
  QUARTERS: "quarter",
  YEARS: "year",
};

const LEGACY_FREQ: Record<string, string> = {
  WEEKLY: "Every week",
  BIWEEKLY: "Every 2 weeks",
  MONTHLY: "Every month",
  QUARTERLY: "Every quarter",
};

function formatFrequency(frequency: string): string {
  const legacy = LEGACY_FREQ[frequency];
  if (legacy) return legacy;

  const parts = frequency.split("_");
  if (parts.length === 2 && !isNaN(Number(parts[0]))) {
    const interval = Number(parts[0]);
    const unitLabel = UNIT_LABELS[parts[1]] || parts[1].toLowerCase();
    if (interval === 1) return `Every ${unitLabel}`;
    return `Every ${interval} ${unitLabel}s`;
  }
  return frequency;
}

const TYPE_BADGE_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-purple-100 text-purple-700",
  "bg-green-100 text-green-700",
  "bg-orange-100 text-orange-700",
  "bg-teal-100 text-teal-700",
  "bg-pink-100 text-pink-700",
];

function typeBadgeColor(type: string): string {
  let hash = 0;
  for (let i = 0; i < type.length; i++) {
    hash = type.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TYPE_BADGE_COLORS[Math.abs(hash) % TYPE_BADGE_COLORS.length];
}

interface ObligationsPanelProps {
  clientId: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export const ObligationsPanel: React.FC<ObligationsPanelProps> = ({
  clientId,
  collapsed,
  onToggleCollapse,
}) => {
  const {
    obligations,
    loading,
    error,
    refresh,
    createObligation,
    updateObligation,
    deleteObligation,
    completeObligation,
    getHistory,
  } = useObligations({ clientId });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingObligation, setEditingObligation] =
    useState<Obligation | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [historyMap, setHistoryMap] = useState<
    Record<string, Completion[]>
  >({});
  const [loadingHistory, setLoadingHistory] = useState<string | null>(null);

  const handleOpenCreate = () => {
    setEditingObligation(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (obligation: Obligation) => {
    setEditingObligation(obligation);
    setModalOpen(true);
  };

  const handleSave = async (data: {
    obligationType: string;
    frequency: string;
    nextDueAt: string;
  }) => {
    if (editingObligation) {
      await updateObligation(editingObligation.id, data);
    } else {
      await createObligation(data);
    }
  };

  const handleDeleteClick = (obligationId: string) => {
    setDeletingId(obligationId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;
    setIsDeleting(true);
    try {
      await deleteObligation(deletingId);
      setDeleteConfirmOpen(false);
      setDeletingId(null);
    } catch {
      // stay open on failure
    } finally {
      setIsDeleting(false);
    }
  };

  const handleComplete = async (obligationId: string) => {
    setCompletingId(obligationId);
    try {
      await completeObligation(obligationId);
    } catch {
      // stays in list on failure
    } finally {
      setCompletingId(null);
    }
  };

  // History shows in the row's view modal (TD-004); fetch lazily on open.
  const loadHistory = async (obligationId: string) => {
    if (historyMap[obligationId]) return;
    setLoadingHistory(obligationId);
    try {
      const completions = await getHistory(obligationId);
      setHistoryMap((prev) => ({ ...prev, [obligationId]: completions }));
    } catch {
      // silently fail
    } finally {
      setLoadingHistory(null);
    }
  };

  const getDueStatus = (obligation: Obligation) => {
    if (!obligation.nextDueAt) return "unknown";
    const due = new Date(obligation.nextDueAt);
    const now = new Date();
    const diffDays = Math.ceil(
      (due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (diffDays < 0) return "overdue";
    if (diffDays <= 3) return "due-soon";
    return "upcoming";
  };

  const formatDueDate = (dateStr: string | null) => {
    if (!dateStr) return "No date";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    const formatted = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    if (diffDays < 0) return `${formatted} (${Math.abs(diffDays)}d overdue)`;
    if (diffDays === 0) return `${formatted} (today)`;
    if (diffDays === 1) return `${formatted} (tomorrow)`;
    if (diffDays <= 7) return `${formatted} (${diffDays}d)`;
    return formatted;
  };

  const formatCompletionDate = (dateStr: string | null) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const dueStatusClasses: Record<string, string> = {
    overdue: "text-red-600 font-medium",
    "due-soon": "text-yellow-600 font-medium",
    upcoming: "text-gray-500",
    unknown: "text-gray-400",
  };

  const summary = useMemo(() => {
    const now = Date.now();
    const week = now + 7 * 24 * 60 * 60 * 1000;
    let overdue = 0;
    let dueWeek = 0;
    for (const o of obligations) {
      if (!o.nextDueAt) continue;
      const due = new Date(o.nextDueAt).getTime();
      if (due < now) overdue += 1;
      else if (due <= week) dueWeek += 1;
    }
    if (overdue > 0) return `${overdue} overdue · ${dueWeek} due this week`;
    if (dueWeek > 0) return `${dueWeek} due this week`;
    return obligations.length > 0 ? "on track" : "none set up";
  }, [obligations]);

  return (
    <>
      <WorkspacePanel
        id="obligations"
        title="Obligations"
        icon={<CalendarCheckIcon className="w-4 h-4" />}
        summary={summary}
        loading={loading}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        className={collapsed ? "" : "h-full"}
        bodyClassName="min-h-[48rem] max-h-[48rem]"
        actions={
          <PanelHeaderAction
            label="Add"
            icon={<PlusIcon className="w-4 h-4" />}
            onClick={handleOpenCreate}
          />
        }
      >
        <>
      {error && (
        <div className="text-center py-6">
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={refresh}
            className="mt-2 text-sm text-blue-600 hover:underline"
          >
            Try again
          </button>
        </div>
      )}

      {!error && (
        <PanelList
          items={obligations}
          keyFor={(o) => o.id}
          empty={
            <div className="text-center py-6">
              <div className="text-gray-400 mb-2">
                <CalendarCheckIcon className="w-8 h-8 mx-auto" />
              </div>
              <p className="text-sm font-medium text-gray-900">
                No obligations set up
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Add recurring obligations like monthly calls or report deliveries
              </p>
            </div>
          }
          renderItem={(obligation) => {
        const status = getDueStatus(obligation);
        const history = historyMap[obligation.id];

        return (
          <PanelListItem
            title={
              <span
                className={`px-2 py-0.5 text-xs rounded-full font-medium ${typeBadgeColor(obligation.obligationType)}`}
              >
                {obligation.obligationType}
              </span>
            }
            meta={
              <>
                <span className="text-xs text-gray-400">
                  {formatFrequency(obligation.frequency)}
                </span>
                <span className={`text-sm ${dueStatusClasses[status]}`}>
                  Due: {formatDueDate(obligation.nextDueAt)}
                </span>
              </>
            }
            preview={
              obligation.lastCompletedAt
                ? `Last completed: ${formatCompletionDate(obligation.lastCompletedAt)}`
                : undefined
            }
            trailing={
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleOpenEdit(obligation)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    title="Edit obligation"
                  >
                    <EditIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(obligation.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Delete obligation"
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleComplete(obligation.id)}
                    disabled={completingId === obligation.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100 transition-colors disabled:opacity-50"
                  >
                    {completingId === obligation.id ? (
                      <SpinnerIcon className="w-3.5 h-3.5" />
                    ) : (
                      <CheckIcon className="w-3.5 h-3.5" />
                    )}
                    Complete
                  </button>
                </div>
            }
            detailTitle={obligation.obligationType}
            onDetailOpen={() => loadHistory(obligation.id)}
            detail={
              <div className="space-y-3">
                <p className={`text-sm ${dueStatusClasses[status]}`}>
                  Due: {formatDueDate(obligation.nextDueAt)}
                </p>
                <div>
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em] mb-2">
                    History
                  </p>
                  {loadingHistory === obligation.id ? (
                    <div className="flex justify-center py-2">
                      <SpinnerIcon className="w-4 h-4 text-gray-400" />
                    </div>
                  ) : history && history.length > 0 ? (
                    <div className="space-y-1.5">
                      {history.map((c) => (
                        <div
                          key={c.id}
                          className="flex items-center justify-between text-xs text-gray-500"
                        >
                          <span>
                            {formatCompletionDate(c.completedAt)}
                            {c.notes && (
                              <span className="text-gray-400 ml-2">
                                {c.notes}
                              </span>
                            )}
                          </span>
                          <span className="text-gray-400">
                            {c.completedBy}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 py-1">
                      No completions yet
                    </p>
                  )}
                </div>
              </div>
            }
          />
        );
          }}
        />
      )}
        </>
      </WorkspacePanel>

      <ObligationModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingObligation(null);
        }}
        onSave={handleSave}
        obligation={editingObligation}
      />

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDeletingId(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Obligation"
        message="Are you sure you want to delete this obligation? All completion history will be removed. This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
      />
    </>
  );
};
